// TrimbleMaps is loaded via CDN in index.html and available as window.TrimbleMaps
// TrimbleMapsControl.Draw is loaded via CDN add-on

require('qs-hash');
const geojsonRewind = require('@mapbox/geojson-rewind');
const { EditControl, SaveCancelControl, TrashControl } = require('./controls');
const { geojsonToLayer, bindPopup } = require('./util');
const styles = require('./styles');
const {
  DEFAULT_STYLE,
  DEFAULT_DARK_FEATURE_COLOR,
  DEFAULT_LIGHT_FEATURE_COLOR,
  DEFAULT_SATELLITE_FEATURE_COLOR,
  DEFAULT_3D_BUILDINGS,
  TRIMBLE_API_KEY
} = require('../../constants');

let writable = false;
let drawing = false;
let editing = false;

const dummyGeojson = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [0, 0]
      }
    }
  ]
};

module.exports = function (context, readonly) {
  writable = !readonly;

  // keyboard shortcuts
  const keybinding = d3
    .keybinding('map')
    // delete key triggers draw.trash()
    .on('⌫', () => {
      if (editing && context.Draw) {
        context.Draw.trash();
      }
    })
    .on('m', () => {
      if (!editing && context.Draw) {
        drawing = true;
        context.Draw.changeMode('draw_point');
      }
    })
    .on('l', () => {
      if (!editing && context.Draw) {
        drawing = true;
        context.Draw.changeMode('draw_line_string');
      }
    })
    .on('p', () => {
      if (!editing && context.Draw) {
        drawing = true;
        context.Draw.changeMode('draw_polygon');
      }
    });

  d3.select(document).call(keybinding);

  function maybeShowEditControl() {
    // if there are features, show the edit button
    if (context.data.hasFeatures()) {
      d3.select('.edit-control').style('display', 'block');
    }
  }

  function map() {
    // Get TrimbleMaps from global scope (loaded via CDN)
    const TrimbleMaps = window.TrimbleMaps;

    if (!TrimbleMaps) {
      console.error(
        'TrimbleMaps SDK not loaded. Please ensure the SDK is included in the HTML.'
      );
      return;
    }

    // Set Trimble Maps API Key (v4 API)
    TrimbleMaps.setAPIKey(TRIMBLE_API_KEY);

    const activeStyle = context.storage.get('style') || DEFAULT_STYLE;

    // Find the style configuration
    const foundStyle = styles.find((d) => d.title === activeStyle);
    const styleConfig =
      foundStyle || styles.find((d) => d.title === 'TRANSPORTATION');

    // Determine the style to use - either a TrimbleMaps enum style or custom style object
    let mapStyle;
    if (
      typeof styleConfig.style === 'string' &&
      TrimbleMaps.Common &&
      TrimbleMaps.Common.Style
    ) {
      mapStyle = TrimbleMaps.Common.Style[styleConfig.style];
    } else {
      mapStyle = styleConfig.style;
    }

    context.map = new TrimbleMaps.Map({
      container: 'map',
      style: mapStyle,
      region: TrimbleMaps.Common.Region.WW,
      center: [-75, 40],
      zoom: 9
    });

    if (writable) {
      // Get TrimbleMapsControl from global scope (loaded via CDN add-on)
      const TrimbleMapsControl = window.TrimbleMapsControl;

      // Add SingleSearch control for geocoding
      if (TrimbleMapsControl && TrimbleMapsControl.SingleSearch) {
        const singleSearchControl = new TrimbleMapsControl.SingleSearch({
          placeholderText: 'Search here',
          doneTypingInterval: 700,
          hideResults: true,
          minSearchTextLength: 3,
          locationRelevance: 'mapCenter',
          searchOption: {
            region: TrimbleMaps.Common.Region.WW,
            maxResults: 30
          },
          markerStyle: {
            graphicHeight: 26,
            graphicWidth: 26,
            graphicXOffset: -13,
            graphicYOffset: -13
          },
          markerConfig: {
            exclusive: true,
            centerOnMap: true,
            zoomLevel: 12
          },
          popupConfig: {
            offset: 15,
            show: true,
            className: 'single-search',
            closeBox: true
          }
        });
        context.map.addControl(singleSearchControl, 'top-right');
      } else {
        console.warn(
          'TrimbleMaps SingleSearch add-on not loaded. Geocoding control will not be available.'
        );
      }
      context.map.addControl(new TrimbleMaps.NavigationControl());
      if (!TrimbleMapsControl || !TrimbleMapsControl.Draw) {
        console.error(
          'TrimbleMaps Draw add-on not loaded. Please ensure the add-on is included in the HTML.'
        );
      } else {
        // Initialize TrimbleMaps Draw control
        context.Draw = new TrimbleMapsControl.Draw({
          displayControlsDefault: false,
          controls: {
            point: true,
            line_string: true,
            polygon: true,
            trash: false
          },
          defaultMode: 'simple_select',
          userProperties: true
        });

        // Add draw control to map
        context.map.addControl(context.Draw, 'top-right');
      }

      

      const editControl = new EditControl();
      context.map.addControl(editControl, 'top-right');

      const saveCancelControl = new SaveCancelControl();
      context.map.addControl(saveCancelControl, 'top-right');

      const trashControl = new TrashControl();
      context.map.addControl(trashControl, 'top-right');

      const exitEditMode = () => {
        editing = false;
        // show the data layers (check if they exist first)
        if (context.map.getLayer('map-data-fill')) {
          context.map.setLayoutProperty(
            'map-data-fill',
            'visibility',
            'visible'
          );
        }
        if (context.map.getLayer('map-data-fill-outline')) {
          context.map.setLayoutProperty(
            'map-data-fill-outline',
            'visibility',
            'visible'
          );
        }
        if (context.map.getLayer('map-data-line')) {
          context.map.setLayoutProperty(
            'map-data-line',
            'visibility',
            'visible'
          );
        }

        // show markers
        d3.selectAll('.trimblemaps-marker').style('display', 'block');

        // clean up draw
        if (context.Draw) {
          context.Draw.changeMode('simple_select');
          context.Draw.deleteAll();
        }

        // hide the save/cancel control and the delete control
        d3.select('.save-cancel-control').style('display', 'none');
        d3.select('.trash-control').style('display', 'none');

        // show the edit button and draw tools
        maybeShowEditControl();
        d3.select('.trimblemaps-ctrl-group:nth-child(3)').style(
          'display',
          'block'
        );
      };

      // handle save or cancel from edit mode
      d3.selectAll('.trimblemaps-draw-actions-btn').on('click', function () {
        const target = d3.select(this);
        const isSaveButton = target.classed(
          'trimblemaps-draw-actions-btn_save'
        );
        if (isSaveButton && context.Draw) {
          const FC = context.Draw.getAll();
          context.data.set(
            {
              map: {
                ...FC,
                features: stripIds(FC.features)
              }
            },
            'map'
          );
        }

        exitEditMode();
      });

      // handle delete - use document-level event delegation for dynamically shown elements
      document.addEventListener('click', (e) => {
        if (e.target.classList.contains('trimblemapsgl-draw_trash')) {
          if (context.Draw) {
            context.Draw.trash();
          }
        }
      });

      // enter edit mode
      d3.selectAll('.trimblemapsgl-draw_edit').on('click', () => {
        if (!context.Draw) return;

        editing = true;
        // hide the edit button and draw tools
        d3.select('.edit-control').style('display', 'none');
        d3.select('.trimblemaps-ctrl-group:nth-child(3)').style(
          'display',
          'none'
        );

        // show the save/cancel control and the delete control
        d3.select('.save-cancel-control').style('display', 'block');
        d3.select('.trash-control').style('display', 'block');

        // hide the line and polygon data layers (check if they exist first)
        if (context.map.getLayer('map-data-fill')) {
          context.map.setLayoutProperty('map-data-fill', 'visibility', 'none');
        }
        if (context.map.getLayer('map-data-fill-outline')) {
          context.map.setLayoutProperty(
            'map-data-fill-outline',
            'visibility',
            'none'
          );
        }
        if (context.map.getLayer('map-data-line')) {
          context.map.setLayoutProperty('map-data-line', 'visibility', 'none');
        }

        // hide markers
        d3.selectAll('.trimblemaps-marker').style('display', 'none');

        // import the current data into draw for editing
        const featureIds = context.Draw.add(context.data.get('map'));
        context.Draw.changeMode('simple_select', {
          featureIds
        });
      });
    }

    context.map.on('idle', () => {
      if (
        context.data.get('mapStyleLoaded') &&
        !context.map.getSource('map-data')
      ) {
        let color = DEFAULT_DARK_FEATURE_COLOR; // Sets default dark color for lighter base maps

        // switch to feature color based on style
        const currentStyle = context.storage.get('style') || DEFAULT_STYLE;

        if (currentStyle === 'DATADARK') {
          color = DEFAULT_LIGHT_FEATURE_COLOR;
        }

        if (currentStyle === 'SATELLITE') {
          color = DEFAULT_SATELLITE_FEATURE_COLOR;
        }

        context.map.addSource('map-data', {
          type: 'geojson',
          data: dummyGeojson
        });

        context.map.addLayer({
          id: 'map-data-fill',
          type: 'fill',
          source: 'map-data',
          paint: {
            'fill-color': ['coalesce', ['get', 'fill'], color],
            'fill-opacity': ['coalesce', ['get', 'fill-opacity'], 0.3]
          },
          filter: ['==', ['geometry-type'], 'Polygon']
        });

        context.map.addLayer({
          id: 'map-data-fill-outline',
          type: 'line',
          source: 'map-data',
          paint: {
            'line-color': ['coalesce', ['get', 'stroke'], color],
            'line-width': ['coalesce', ['get', 'stroke-width'], 2],
            'line-opacity': ['coalesce', ['get', 'stroke-opacity'], 1]
          },
          filter: ['==', ['geometry-type'], 'Polygon']
        });

        context.map.addLayer({
          id: 'map-data-line',
          type: 'line',
          source: 'map-data',
          paint: {
            'line-color': ['coalesce', ['get', 'stroke'], color],
            'line-width': ['coalesce', ['get', 'stroke-width'], 2],
            'line-opacity': ['coalesce', ['get', 'stroke-opacity'], 1]
          },
          filter: ['==', ['geometry-type'], 'LineString']
        });

        geojsonToLayer(context, writable);

        // Initialize 3D buildings state from localStorage after style is loaded
        const hasKey = context.storage.get('3DBuildings') !== undefined;
        const active3DBuildings = hasKey
          ? context.storage.get('3DBuildings')
          : DEFAULT_3D_BUILDINGS;

        // Trimble Maps 3D buildings handling (if supported by current style)
        if (context.map.getLayer && context.map.getLayoutProperty) {
          try {
            // Trimble Maps may have 3D building layer with different name
            const buildingLayers = ['building-extrusion', '3d-buildings'];
            buildingLayers.forEach((layerId) => {
              if (context.map.getLayer(layerId)) {
                context.map.setLayoutProperty(
                  layerId,
                  'visibility',
                  active3DBuildings ? 'visible' : 'none'
                );
              }
            });
          } catch (e) {
            // 3D buildings not available in this style
          }
        }

        // Update the UI to reflect the active state
        d3.selectAll('.toggle-3D button').classed('active', function () {
          const { value } = d3.select(this).datum();
          return value === active3DBuildings;
        });

        context.data.set({
          mapStyleLoaded: false
        });
      }
    });

    // only show 3D Buildings toggle on Zoom > 14
    function updateTogglesByZoom() {
      const zoom = context.map.getZoom();
      const toggle3D = d3.select('.toggle-3D');

      // Get current style to check if 3D buildings should be hidden
      const currentStyle = context.storage.get('style') || DEFAULT_STYLE;
      const shouldHide3DForStyle =
        currentStyle === 'OSM' || currentStyle === 'SATELLITE';

      if (zoom < 14) {
        toggle3D.classed('hidden', true);
      } else {
        // Hide 3D toggle for certain styles, regardless of zoom
        toggle3D.classed('hidden', shouldHide3DForStyle);
      }
    }
    context.map.on('load', () => updateTogglesByZoom());
    context.map.on('zoomend', () => updateTogglesByZoom());

    const maybeSetCursorToPointer = () => {
      if (context.Draw && context.Draw.getMode() === 'simple_select') {
        context.map.getCanvas().style.cursor = 'pointer';
      }
    };

    const maybeResetCursor = () => {
      if (context.Draw && context.Draw.getMode() === 'simple_select') {
        context.map.getCanvas().style.removeProperty('cursor');
      }
    };

    const handleLinestringOrPolygonClick = (e) => {
      // prevent this popup from opening when the original click was on a marker
      const el = e.originalEvent.target;
      if (el.nodeName !== 'CANVAS') return;
      // prevent this popup from opening when drawing new features
      if (drawing) return;

      bindPopup(e, context, writable);
    };

    context.map.on('load', () => {
      context.data.set({
        mapStyleLoaded: true
      });
      context.map.on('mouseenter', 'map-data-fill', maybeSetCursorToPointer);
      context.map.on('mouseleave', 'map-data-fill', maybeResetCursor);
      context.map.on('mouseenter', 'map-data-line', maybeSetCursorToPointer);
      context.map.on('mouseleave', 'map-data-line', maybeResetCursor);

      context.map.on('click', 'map-data-fill', handleLinestringOrPolygonClick);
      context.map.on('click', 'map-data-line', handleLinestringOrPolygonClick);
      context.map.on(
        'touchstart',
        'map-data-fill',
        handleLinestringOrPolygonClick
      );
      context.map.on(
        'touchstart',
        'map-data-line',
        handleLinestringOrPolygonClick
      );
    });

    context.map.on('draw.create', created);

    function stripIds(features) {
      return features.map((feature) => {
        delete feature.id;
        return feature;
      });
    }

    function created(e) {
      if (context.Draw) {
        context.Draw.deleteAll();
      }
      update(stripIds(e.features));

      // delay setting drawing back to false after a drawn feature is created
      // this allows the map click handler to ignore the click and prevents a popup
      // if the drawn feature ends within an existing feature
      setTimeout(() => {
        drawing = false;
      }, 500);
    }

    function update(features) {
      let FC = context.data.get('map');

      FC.features = [...FC.features, ...features];

      FC = geojsonRewind(FC);

      context.data.set({ map: FC }, 'map');
    }

    context.dispatch.on('change.map', ({ obj }) => {
      maybeShowEditControl();
      if (obj.map) {
        geojsonToLayer(context, writable);
      }
    });
  }

  return map;
};
